import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import _ from 'lodash'
import { Button, Icon } from '@lmc/ui-kit'
import { colors, sizes } from '@lmc/common/constants'
import Operations from '../../../../operations'
import { formatAddress, showToast } from '../../../../common/utils'
import { isBrowser, isTablet } from 'react-device-detect'
import {
  ProfileField,
  ProfilePicture,
  Spinner
} from '../../../../components/generic'
import ResidentProfileSectionWrapper from '../../../Residents/screens/ResidentProfileScreen/_legacyReidentProfileScreen/components/ResidentProfileSectionWrapper'
import { postJSON } from '../../../../common/services/api'
import { withRouter } from '../../../../withRouter'
import { history } from '../../../../store'

const isDesktop = isBrowser || isTablet
const SITE_PLACEHOLDER =
  'https://lmc-data-production.s3.eu-west-2.amazonaws.com/public/house_placeholder.png'

const redirect = (id) => {
  history.push(`/homes/${id}/edit`)
}
const renderFooter = (router) => (
  <div
    className="lmc-fixed-footer"
    style={{
      ...styles.footer
    }}
  >
    <Button
      style={styles.footerButtonPadding}
      variant="default"
      onClick={() => {
        if (window.Intercom) window.Intercom('showNewMessage')
      }}
    >
      {isDesktop ? 'Transfer Resident' : 'Transfer'}
    </Button>
    <Button onClick={() => redirect(router.params.id)} variant="primary">
      Edit site
    </Button>
  </div>
)

const renderFields = (home) => (
  <div className="lmc-card" style={styles.fieldsContainer}>
    <div style={styles.infoContainer}>
      <span style={styles.name}>{home.name}</span>
      <ProfileField
        label="Address"
        value={home.address && formatAddress(home.address)}
        style={{ marginTop: 18 }}
      />
      <ProfileField label="Telephone" value={home.telephone} />
      <ProfileField
        label="Number of team members"
        value={`${home.activeCarers || 0}`}
      />
      <ProfileField
        label="Number of active residents"
        value={`${home.activeResidents || 0}`}
      />
    </div>
  </div>
)

const TextField = ({ label, text, isLast }) => (
  <div>
    <p style={{ fontWeight: 'bold', fontSize: 12, margin: 0 }}>{label}</p>
    <p style={{ margin: 0, fontSize: 14, color: colors.darkgrey }}>{text}</p>
    <hr style={{ marginTop: 6, marginBottom: isLast ? 0 : 10 }} />
  </div>
)

const renderCarers = (carers) => (
  <ResidentProfileSectionWrapper
    color={colors.orange}
    icon="social"
    style={styles.carersContainer}
    title="Site Managers"
  >
    {carers && carers.length ? (
      carers.map((carer) => (
        <div className="lmc-card" key={carer.id} style={styles.carerRow}>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div style={{ flex: 1, maxWidth: 50 }}>
              <ProfilePicture
                type="small"
                url={carer.picture}
                style={{
                  margin: 0
                  // ...chosenImgStyle,
                  // ...styles.image
                }}
              />
            </div>
            <div style={{ flex: 10 }}>
              <TextField
                label="Name"
                text={`${carer?.name.first} ${carer?.name.last}`}
              />
              <TextField label="Email" text={carer.email} isLast />
            </div>
          </div>
        </div>
      ))
    ) : (
      <Spinner />
    )}
  </ResidentProfileSectionWrapper>
)

const handleExport = (userEmail, homeId, setLoadingState) => {
  setLoadingState(true)
  postJSON({ url: `services/csv/export-all/${homeId}` })
    .then(() => {
      setLoadingState(false)
      showToast({
        title: `An email with the CSV will be sent to ${userEmail}. At peak times this may take a while.`
      })
    })
    .catch(() => setLoadingState(false))
}

export function HomeProfile(props) {
  const { home, carers, fetchData, router, userEmail } = props

  const [isLoading, setLoadingState] = React.useState(true)
  const homeId = router.params.id

  React.useEffect(() => {
    setLoadingState(true)
    fetchData(homeId)
      .then(() => setLoadingState(false))
      .catch(() => setLoadingState(false))
  }, [homeId])

  if ((!carers || !carers.length) && isLoading) return <Spinner />

  return (
    <div style={styles.container}>
      <div style={styles.topButtonContainer}>
        <Button
          variant="default"
          loading={isLoading}
          onClick={() => handleExport(userEmail, homeId, setLoadingState)}
          icon={<Icon name="CloudDownloadOutlined" />}
        >
          Export CSV
        </Button>
      </div>
      <div style={styles.wrapper}>
        <ProfilePicture
          type="large"
          url={home.picture || SITE_PLACEHOLDER}
          style={styles.image}
        />
        {renderFields(home)}
        {renderCarers(carers)}
      </div>
      {renderFooter(router)}
    </div>
  )
}

const styles = {
  container: {
    paddingTop: 24,
    height: '100%',
    maxWidth: sizes.childScreen,
    position: 'relative',
    margin: '0 auto'
  },
  wrapper: {
    padding: '0 0 125px'
  },
  topButtonContainer: {
    display: 'flex',
    justifyContent: 'end'
  },
  footerButtonPadding: {
    paddingRight: 8
  },
  button: {
    background: colors.green,
    color: 'white',
    padding: '0px 25px',
    marginLeft: 10,
    textShadow: 'none'
  },
  infoContainer: {
    textAlign: 'center'
  },
  name: {
    fontWeight: 600,
    fontSize: 28
  },
  fieldsContainer: {
    paddingTop: 80,
    position: 'relative',
    bottom: 70
  },
  footer: {
    border: 'none',
    background: 'white',
    display: 'flex',
    justifyContent: 'flex-end',
    width: isBrowser || isTablet ? 'calc(100vw - 290px)' : '100vw'
  },
  carersContainer: {
    position: 'relative',
    bottom: 30
  },
  carerRow: {
    marginTop: 8
  },
  image: {
    position: 'relative',
    zIndex: 1
  },
  transferIcon: {
    fontSize: 16
  }
}

HomeProfile.propTypes = {
  carers: PropTypes.array.isRequired,
  fetchData: PropTypes.func.isRequired,
  home: PropTypes.object,
  router: PropTypes.object.isRequired,
  userEmail: PropTypes.string
}

const mapStateToProps = (state, props) => {
  const { id } = props.router.params
  return {
    carers: _.chain(state.data.carers)
      .filter(
        (carer) =>
          carer.home === id || (carer.homes && carer.homes.includes(id))
      )
      .orderBy('name.first')
      .value(),
    home: _.find(state.data.homes, { id }),
    userEmail: state.global.user.email
  }
}

const mapDispatchToProps = (dispatch) => ({
  fetchData: (homeID) =>
    Promise.all([
      Operations.loadList('carers', { home: homeID }, false)(dispatch),
      // fetching home object with Carer and Resident counts
      Operations.loadListItem('homes', homeID)(dispatch)
    ])
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(HomeProfile)
)
